import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useBackState, useScrollMemory } from "@/hooks/useScrollMemory";
import { UserName } from "@/components/UserName";
import { SalesThumb, SalesTitle, StateChip, Missing } from "@/components/sales/SalesRow";
import { Pager } from "@/components/sales/RecommendedTab";
import { FileText, Loader2 } from "lucide-react";

// THE QUEUE (C3): a to-do list — photo, name, state, what's missing, who put it there. Filters by state, type and
// person, each with its count; tap to filter, tap again to clear. Sold and Hidden are filters, not screens.
const KIND_WORDS = { part: "Parts", truck: "Trucks" };

export function QueueTab({ onOpen }) {
  const [state, setState] = useBackState("q-state", "");
  const [kind, setKind] = useBackState("q-kind", "");
  const [person, setPerson] = useBackState("q-person", "");
  const [page, setPage] = useBackState("q-page", 1);
  const [res, setRes] = useState(null);
  useEffect(() => {
    api.get("/sales/queue", { params: { state: state || undefined, kind: kind || undefined, person: person || undefined, page, page_size: 50 } })
      .then((r) => setRes(r.data)).catch(() => setRes({ rows: [], total: 0, counts: {}, states: [] }));
  }, [state, kind, person, page]);
  useScrollMemory(res != null);
  if (!res) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>;
  const pick = (set, cur, v) => { set(cur === v ? "" : v); setPage(1); };
  const words = Object.fromEntries((res.states || []).map((s) => [s.key, s.words]));
  return (
    <div data-testid="sales-queue" className="space-y-3">
      <Chips testid="sales-filter-state" counts={res.counts.state} words={words} cur={state} onPick={(v) => pick(setState, state, v)} />
      <div className="flex flex-wrap gap-4">
        <Chips testid="sales-filter-kind" counts={res.counts.kind} words={KIND_WORDS} cur={kind} onPick={(v) => pick(setKind, kind, v)} />
        <Chips testid="sales-filter-person" counts={res.counts.person} cur={person} onPick={(v) => pick(setPerson, person, v)} />
      </div>
      <p data-testid="sales-queue-total" className="text-sm text-slate-600"><b className="text-slate-900">{res.total}</b> in the sales queue{state || kind || person ? " with these filters" : ""}</p>
      {!res.rows.length && <p data-testid="sales-queue-empty" className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">Nothing in the sales queue{state || kind || person ? " with these filters" : " yet"}. A complete part goes in when someone prepares it from Recommended to sell or nominates it from its Sales menu; a truck goes in with Recon finished or Sell this truck whole.</p>}
      {res.rows.map((r) => <QueueRow key={`${r.kind}-${r.id}`} r={r} onOpen={onOpen} />)}
      <Pager page={page} setPage={setPage} total={res.total} size={50} testid="sales-queue" />
    </div>
  );
}

function Chips({ counts = {}, words = {}, cur, onPick, testid }) {
  const keys = Object.keys(counts);
  if (!keys.length) return null;
  return (
    <div data-testid={testid} className="flex flex-wrap gap-1.5">
      {keys.map((k) => (
        <button key={k} type="button" data-testid={`${testid}-${k}`} onClick={() => onPick(k)}
          className={`h-10 px-3 rounded-full border text-sm font-semibold ${cur === k ? "bg-[#1F1F1F] text-white border-[#1F1F1F]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
          {words[k] || k} <span className="font-mono opacity-70">{counts[k]}</span>
        </button>
      ))}
    </div>
  );
}

function QueueRow({ r, onOpen }) {
  return (
    <div data-testid={`sales-queue-row-${r.kind}-${r.id}`} data-back-row className="rounded-xl border border-slate-200 bg-white p-3 flex gap-3">
      <SalesThumb fileId={r.photo_file_id} testid={`sales-queue-photo-${r.id}`} />
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <SalesTitle row={r} testid={`sales-queue-${r.id}`} />
          <StateChip state={r.state} words={r.state_words} testid={`sales-queue-state-${r.id}`} />
        </div>
        <p data-testid={`sales-queue-why-${r.id}`} className="text-xs text-slate-600">{r.reason}{r.by ? <> · <UserName email={r.by} /></> : null}{r.at ? ` · ${r.at.slice(0, 10)}` : ""}</p>
        <Missing missing={r.missing} testid={`sales-queue-missing-${r.id}`} lead="To fix" />
        {r.state !== "sold" && r.state !== "hidden" && (
          <button type="button" data-testid={`sales-queue-draft-${r.id}`} onClick={() => onOpen(`${r.kind}:${r.id}`)}
            className="inline-flex items-center gap-1.5 h-11 px-4 rounded-lg border-2 border-slate-300 bg-white text-sm font-bold hover:bg-slate-50">
            <FileText className="w-4 h-4" />Listing draft</button>
        )}
      </div>
    </div>
  );
}
